import React, { useEffect, useState } from "react";
import { useCallback, useMemo } from "react";
import { Column, TableInstance } from "react-table";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IDealer, IDealerLocation } from "../../models/Dealer";
import { ReactTable } from "../../components/table/ReactTable";
import { apiStore } from "../../api/ApiFactory";
import { formatDate } from "../../helpers/utils/formatDate";
import { useAuth } from "../../store/hooks/useAuth";
import { Roles } from "../../helpers/enums/AuthConstants";
import { getValueByKey } from "../../helpers/utils/getEnumValueKey";
import { DefaultCostType } from "../../helpers/enums/Common";
import {
  Checkbox,
  Chip,
  Grid,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
} from "@mui/material";
import { CustomTooltip } from "../../components/tooltip/CustomTooltip";
import { SelectColumnFilter } from "../../components/table/filters/SelectColumnFilter";
import { NameValueEntity } from "../../models/Common";

const DealerTable = () => {
  let navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  let authuser = useAuth().authuser;

  const displayLocations = (value: Array<IDealerLocation>) => (
    <CustomTooltip
      maxwidth={500}
      enterDelay={400}
      title={
        <React.Fragment>
          <Grid container spacing={1}>
            {value.map((location, index: number) => (
              <Grid item xs={12} key={index}>
                <Typography>
                  {location.name} (
                  {location.isActive ? location.customerID : "Inactive"})
                </Typography>
                <Grid>
                  <Typography sx={{ fontSize: "11px" }}>
                    Sales Rep: {location.salesRepCode}
                  </Typography>
                </Grid>
                {(location.address1 ||
                  location.address2 ||
                  location.zipCode) && (
                  <Grid sx={{ marginTop: "3px" }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      Address:
                    </Typography>
                    {location.address1 && (
                      <Typography sx={{ fontSize: "11px" }}>
                        {location.address1}
                      </Typography>
                    )}
                    {location.address2 && (
                      <Typography sx={{ fontSize: "11px" }}>
                        {location.address2}
                      </Typography>
                    )}
                    {location.zipCode && (
                      <Typography sx={{ fontSize: "11px" }}>
                        {location.city}, {location.country} {location.state}{" "}
                        {location.zipCode}{" "}
                      </Typography>
                    )}
                  </Grid>
                )}
                {location.phone && (
                  <Grid sx={{ marginTop: "3px" }}>
                    <Typography sx={{ fontSize: "11px" }}>
                      Phone: {location.phone}
                    </Typography>
                  </Grid>
                )}

                {value[index + 1] && (
                  <Grid sx={{ textAlign: "center" }}>* * *</Grid>
                )}
              </Grid>
            ))}
          </Grid>
        </React.Fragment>
      }
    >
      <div>{value.length}</div>
    </CustomTooltip>
  );

  const columns: Column<IDealer>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Default Cost Type",
        accessor: "defaultCostType",
        Cell: (cell: any) =>cell.value !== "" ? getValueByKey(cell.value, DefaultCostType) : "",
        Filter: SelectColumnFilter,
        filterOptions: DefaultCostType.map((item): NameValueEntity => {
          return {name: item.value, value: item.key}
        })

      },
      {
        Header: "SP - Model",
        accessor: "salePriceMarkupModel",
        Cell: (cell: any) =>
          cell?.value ? Math.trunc(cell?.value * 100) + "%" : "0%",
      },
      {
        Header: "SP - Option",
        accessor: "salePriceMarkupOption",
        Cell: (cell: any) =>
          cell?.value ? Math.trunc(cell?.value * 100) + "%" : "0%",
      },
      {
        Header: "SP - Motor",
        accessor: "salePriceMarkupMotor",
        Cell: (cell: any) =>
          cell?.value ? Math.trunc(cell?.value * 100) + "%" : "0%",
      },
      {
        Header: "Available for Leads",
        accessor: "isAvailableForLeads",
        Cell: (cell: any) => (
          <Checkbox
            className="row-checkbox"
            disabled
            checked={cell.value ? true : false}
          />
        ),
      },
      {
        Header: "Locations",
        accessor: "locations",
        Filter: SelectColumnFilter,
        filter: "inlist",
        Cell: (cell: any) => <div>{displayLocations(cell.value)}</div>,
      },
      {
        Header: "Is Legacy",
        accessor: "isLegacy",
        Cell: (cell: any) => {
          return (
            <Checkbox
              className="row-checkbox"
              disabled
              checked={cell.value}
              value={cell.value}
            />
          );
        },
        width: 50,
      },
      {
        Header: "Created Date",
        accessor: "createdDate",
        Cell: (cell: any) => formatDate(cell.value),
      },
    ],
    []
  );
  const AddRow = useCallback(
    (instance: TableInstance<IDealer>) => () => {
      navigate("/dealer/add");
    },
    []
  );

  const ViewRow = useCallback(
    (instance: TableInstance<IDealer>) => () => {
      const dealer = instance.selectedFlatRows[0].original;
      navigate(`/dealer/view/${dealer.id}`);
    },
    []
  );

  useEffect(() => {
    const result = apiStore.dealerApi.getDealerList({includeInactive: true});
    result.then((response) => {
      if (authuser?.user.role == Roles.DEALER) {
        const dealer = response[0];

        if (dealer.id === authuser.user.dealerId) {
          navigate(`/dealer/view/${dealer.id}`);
        }
      }
      setTableData(response);
    });
  }, []);

  return (
    <>
      <ReactTable
        name="order-table"
        columns={columns}
        serverOnChange={false}
        data={tableData ? tableData : []}
        isLoading={false}
        onAdd={AddRow}
        onEdit={ViewRow}
      />
    </>
  );
};

export default DealerTable;
